import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom-v5-compat";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {font, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingLarge,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {
    EmailTextField,
    getSignupErrorMessageByCode,
    isEmailValid,
    PASSWORD_MIN_LENGTH,
    PasswordTextField,
    signupWithPassword,
} from "@khan/auth-password";
import {useGql} from "@khan/data-gql";
import {standardTransition} from "@khan/shared-styles";
import {getContinueUrl, parseUrl, redirect, useLocation} from "@khan/urls";

import {BackLink} from "./back-link.tsx";
import {SignUpLegalCheckbox} from "./signup-legal-checkbox.tsx";
import {SignupOauthButtons} from "./signup-oauth-buttons.tsx";

import warningCircleIcon from "@phosphor-icons/core/bold/warning-circle-bold.svg";

// eslint-disable-next-line react-refresh/only-export-components -- used elsewhere
export enum Roles {
    PARENT = "parent",
    TEACHER = "teacher",
    LEARNER = "learner",
}

interface Props {
    /**
     * The top level heading text to display
     */
    heading: string;
    /**
     * Text which displays below the heading. Provides extra info to the user
     */
    subHeading?: string;
    /**
     * The path to navigate to after a user signs up. Should be preceded by "/"
     */
    nextPath: string;
    /**
     * The role the user is signing up as
     */
    role: Roles;
    /**
     * The legal blurb that displays beneath the signup button
     */
    legalBlurb: React.ReactNode;
    /**
     * The text for the back link
     */
    backlinkText?: string;
    /**
     * The path for the back link. Note this is also used to redirect a user if
     * they do not have a birthday or the proper role.
     */
    backlinkPath: string;
    /**
     * Hide the back link or not
     */
    hideBackLink?: boolean;
    /**
     * Whether the current page is the homepage
     */
    isHomepage?: boolean;
    maxWidth?: string;
    /**
     * Callback triggered after a user has signed up, but before they are
     * redirected to the next page.
     */
    onSubmitSuccess?: () => Promise<void>;
    /**
     * A component for the user to login with an existing account. If not provided,
     * the login component will not be rendered.
     */
    loginComponent?: React.ReactNode;
    /**
     * If there isn't a continue= query param, use this path as the default
     */
    defaultContinuePath?: string;
    /**
     * The class code to use for sign up.
     */
    classCode?: string;
    /**
     * The alignment of the heading.
     */
    headingAlignment?: "left" | "center";
}

/**
 * A form for the user to enter their username and password during sign up.
 *
 * Also includes the option to sign up with a 3rd parties such as Google.
 */
export const SignupForm = ({
    heading,
    subHeading,
    nextPath,
    role,
    legalBlurb,
    backlinkText,
    backlinkPath,
    hideBackLink = false,
    isHomepage = false,
    maxWidth = "100%",
    onSubmitSuccess,
    defaultContinuePath = "/",
    classCode,
    headingAlignment = "left",
    loginComponent,
}: Props) => {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [oauthError, setOauthError] = useState("");
    const [checked, setChecked] = useState(false);
    const [signupValidationError, setSignupValidationError] = useState("");
    const [oauthValidationError, setOauthValidationError] = useState("");
    const [signupError, setSignupError] = useState<string | React.ReactElement>(
        "",
    );
    const navigate = useNavigate();
    const location = useLocation();
    const gqlFetch = useGql();
    const tosCheckboxFeatureFlagOn = useFlagOrCookie(
        "signup-tos-checkbox-required",
    );
    const birthdate = useMemo(() => {
        return sessionStorage.getItem("new_signup_birthday") ?? "";
    }, []);

    if (!birthdate && role === Roles.LEARNER) {
        navigate(backlinkPath);
        return;
    }

    const onEmailChange = (value: string) => {
        setEmail(value);
    };

    const onPasswordChange = (value: string) => {
        setPassword(value);
    };

    // Callback for SignupOauthButtons when TOS is not accepted
    const handleOAuthValidationError = () => {
        // Clear non oAuth erorrs
        setSignupError("");
        setSignupValidationError("");

        // Set TOS error
        setOauthValidationError(t`You must agree to the terms to proceed.`);
    };

    // Validates whether the user agreed to the terms
    const validateSignUpTerms = (): boolean => {
        // Skip validation if the TOS checkbox feature is turned off
        if (!tosCheckboxFeatureFlagOn) {
            return true;
        }
        if (!checked) {
            // Clear oAuth errors
            setOauthError("");
            setOauthValidationError("");

            // Set TOS error
            setSignupValidationError(
                t`You must agree to the terms to proceed.`,
            );
            return false;
        }

        // If form is valid clear previous form error
        setOauthValidationError("");
        setSignupValidationError("");
        return true;
    };

    const Heading = () => {
        return isHomepage ? (
            <HeadingLarge>{heading}</HeadingLarge>
        ) : (
            <HeadingSmall>{heading}</HeadingSmall>
        );
    };

    const finalRedirectUrl = getContinueUrl(location) ?? defaultContinuePath;
    const continueUrl = parseUrl(nextPath);
    const backUrl = parseUrl(backlinkPath);
    if (finalRedirectUrl && finalRedirectUrl !== defaultContinuePath) {
        continueUrl.searchParams.set("continue", finalRedirectUrl);
        backUrl.searchParams.set("continue", finalRedirectUrl);
    }

    // Preserve classCode across redirects
    if (classCode && tosCheckboxFeatureFlagOn) {
        continueUrl.searchParams.set("classcode", classCode);
        backUrl.searchParams.set("classcode", classCode);
    }

    const onSignup = async () => {
        // Validate terms agreement
        if (!validateSignUpTerms()) {
            return;
        }

        // Check if valid
        if (!isEmailValid(email) || password.length < PASSWORD_MIN_LENGTH) {
            setSignupError(t`Please fill out all required fields.`);
            return;
        }

        try {
            setLoading(true);
            await signupWithPassword(gqlFetch, {
                email,
                password,
                firstname: "",
                lastname: "",
                role,
                birthdate,
                signupCodes: classCode ? [classCode] : undefined,
            });
            sessionStorage.removeItem("new_signup_birthday");

            if (onSubmitSuccess) {
                await onSubmitSuccess();
            }

            redirect(continueUrl.pathname + continueUrl.search);
        } catch (error) {
            if (error instanceof Error) {
                const message = getSignupErrorMessageByCode(error.message);
                setSignupError(message);
            } else {
                setSignupError("An error occurred, please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

    const passwordDescription = t`Password must be at least 8 characters and have a special character`;

    return (
        <form
            action=""
            onSubmit={async (evt: React.SyntheticEvent): Promise<void> => {
                evt.preventDefault();
                await onSignup();
            }}
        >
            <View style={{maxWidth}}>
                {!hideBackLink && (
                    <BackLink
                        text={backlinkText}
                        backUrl={backUrl.pathname + backUrl.search}
                        style={styles.backlink}
                    />
                )}
                <View
                    style={{
                        ...styles.headingContainer,
                        alignItems: headingAlignment,
                    }}
                >
                    <Heading />
                </View>
                {subHeading && (
                    <LabelMedium style={styles.subHeading}>
                        {subHeading}
                    </LabelMedium>
                )}
                {tosCheckboxFeatureFlagOn && (
                    <View style={styles.legalContainer}>
                        <SignUpLegalCheckbox
                            checked={checked}
                            setChecked={setChecked}
                            legalBlurb={legalBlurb}
                        />
                    </View>
                )}
                <ul className={css(styles.buttons, styles.buttonsIconOnly)}>
                    <SignupOauthButtons
                        under13={false}
                        role={role}
                        continueUrl={continueUrl.toString()}
                        iconOnly={true}
                        size="large"
                        setServerError={setOauthError}
                        showFacebookButton={true}
                        validationRequired={tosCheckboxFeatureFlagOn}
                        isFormValid={checked}
                        onValidationError={handleOAuthValidationError}
                    />
                </ul>
                <View
                    role="alert"
                    aria-live="polite"
                    style={styles.errorMessageContainer}
                >
                    {oauthValidationError && (
                        <View style={styles.errorRow}>
                            <PhosphorIcon
                                icon={warningCircleIcon}
                                color={semanticColor.status.critical.foreground}
                            />
                            <Body tag="p" style={styles.errorMessage}>
                                {oauthValidationError}
                            </Body>
                        </View>
                    )}
                    {oauthError && (
                        <View style={styles.errorRow}>
                            <PhosphorIcon
                                icon={warningCircleIcon}
                                color={semanticColor.status.critical.foreground}
                            />
                            <Body tag="p" style={styles.errorMessage}>
                                {oauthError}
                            </Body>
                        </View>
                    )}
                </View>
                <View style={styles.fieldsHeader}>
                    <Body tag="span" style={styles.fieldsHeaderText}>
                        {t`Or sign up with email`}
                    </Body>
                </View>
                <View style={styles.fieldsContainer}>
                    <EmailTextField
                        required={true}
                        onChange={onEmailChange}
                        label={t`Email`}
                        initialValue={email}
                    />
                    <PasswordTextField
                        required={true}
                        label={t`Password`}
                        onChange={onPasswordChange}
                        initialValue={password}
                        description={passwordDescription}
                    />
                    <Button
                        type="submit"
                        style={styles.signupButton}
                        spinner={loading}
                    >
                        {t`Sign up`}
                    </Button>
                </View>
                {!tosCheckboxFeatureFlagOn && (
                    <View style={styles.legalContainer}>{legalBlurb}</View>
                )}

                <View role="alert" aria-live="polite">
                    {signupValidationError && (
                        <View style={styles.errorRow}>
                            <PhosphorIcon
                                icon={warningCircleIcon}
                                color={semanticColor.status.critical.foreground}
                            />
                            <Body tag="p" style={styles.errorMessage}>
                                {signupValidationError}
                            </Body>
                        </View>
                    )}
                    {signupError && (
                        <View style={styles.errorRow}>
                            <PhosphorIcon
                                icon={warningCircleIcon}
                                color={semanticColor.status.critical.foreground}
                            />
                            <Body tag="p" style={styles.errorMessage}>
                                {signupError}
                            </Body>
                        </View>
                    )}
                </View>
                {loginComponent ? loginComponent : null}
            </View>
        </form>
    );
};

const styles = StyleSheet.create({
    headingContainer: {
        marginBlockEnd: sizing.size_160,
    },
    backlink: {
        alignSelf: "flex-start",
        marginBlockEnd: sizing.size_080,
    },
    subHeading: {
        color: semanticColor.core.foreground.neutral.subtle,
        marginBlockEnd: sizing.size_160,
    },
    legalContainer: {
        marginBlockEnd: sizing.size_160,
    },
    errorMessageContainer: {
        marginBlockEnd: sizing.size_320,
    },
    errorRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: sizing.size_080,
    },
    fieldsHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        marginBlockEnd: sizing.size_160,
        ":after": {
            content: "''",
            position: "absolute",
            top: "50%",
            height: 1,
            width: "100%",
            backgroundColor: semanticColor.core.border.neutral.subtle,
        },
    },
    fieldsHeaderText: {
        fontSize: font.size.small,
        backgroundColor: semanticColor.core.background.base.default,
        padding: `0 ${sizing.size_100}`,
        position: "relative",
        zIndex: 1,
    },
    fieldsContainer: {
        gap: sizing.size_200,
        marginBlockEnd: sizing.size_160,
    },
    signupButton: {
        width: "100%",
    },
    buttons: {
        // @ts-expect-error - TS2322 - Type '{ from: { opacity: number; }; to: { opacity: number; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            from: {opacity: 0},
            to: {opacity: 1},
        },
        animationDuration: "500ms",
        width: "100%",
    },
    buttonsIconOnly: {
        display: "flex",
        flexWrap: "wrap",
        columnGap: sizing.size_160,
        rowGap: sizing.size_080,
    },
    errorMessage: {
        color: semanticColor.status.critical.foreground,
        lineHeight: font.lineHeight.small,
        transition: standardTransition,
        transitionProperty: `max-height, opacity`,
        marginBlockStart: sizing.size_0,
        marginBlockEnd: sizing.size_0,
    },
});
